import { ModelPackage, Subscription } from '@zola/svc-marketplace-ts-types';

import { ReduxCreditBalanceView } from '~/actions/vendors/types/vendorCreditActionTypes';
import useCurrentStorefront from '~/components/common/hooks/useCurrentStorefront';
import useCreditBalance from '~/hooks/vendors/useCreditBalance';
import { isFreeTrial, isFreeTrialWithoutPurchasedPlan } from '~/util/creditUtils';

import { useInquiryStats } from './useInquiryStats';
import useRenewalPlan from './useRenewalPlan';

export type UseMonetizationStatusResult = Pick<
  ReduxCreditBalanceView,
  /** what is the total credit balance, free and purchased */
  | 'creditBalance'
  /** Don't use this to determine if credits are active, use the unlimitedCreditsActive, use this to show a date when the credits expire */
  | 'discountActiveUntil'
  | 'discountRecentlyExpired'
  | 'freeCreditOfferAmount'
  /** is their current balance considered low.  This may vary by vendor type */
  | 'lowBalance'
  /** Is an unlimited package active */
  | 'unlimitedCreditsActive'
  /** Is an unlimited plus package active */
  | 'autoConnect'
  /** Has the vendor purchased credits or are they using free trial credits */
  | 'purchaser'
  /** When did the discount expire */
  | 'discountExpiredAt'
> & {
  /** Has the hook completed loading and are the fields considered stable */
  loaded: boolean;

  /** is the storefront published */
  published: boolean;

  /** Renewal Plan props */
  subscription?: Subscription | null;
  recentPackage?: ModelPackage | null;
  upcomingPackage?: ModelPackage | null;
  isFreeTrial: boolean;
  isFreeTrialWithoutPurchasedPlan: boolean;
  renewalPlanLoaded: boolean;
};

/**
 * This hook encapsulates where a storefront is in their credit purchase
 * journey so we can build responsive UIs.
 */
const useMonetizationStatus = (): UseMonetizationStatusResult => {
  const { publishedAt, uuid } = useCurrentStorefront() || {};

  const { loaded: inquiryStatsLoaded } = useInquiryStats();
  const {
    creditBalance,
    discountActiveUntil,
    discountRecentlyExpired,
    freeCreditOfferAmount,
    lowBalance,
    unlimitedCreditsActive,
    autoConnect,
    loaded: creditBalanceLoaded,
    purchaser,
    discountExpiredAt,
  } = useCreditBalance();

  const renewalPlan = useRenewalPlan(uuid as string, Boolean(publishedAt));

  const result = {
    loaded: creditBalanceLoaded && inquiryStatsLoaded,
    published: Boolean(publishedAt),

    // Fields from the credit balance
    creditBalance,
    discountActiveUntil,
    discountRecentlyExpired,
    discountExpiredAt,
    freeCreditOfferAmount,
    lowBalance,
    unlimitedCreditsActive,
    autoConnect,
    purchaser,
  };

  if (renewalPlan) {
    return {
      ...result,
      subscription: renewalPlan.subscription,
      recentPackage: renewalPlan.recentPackage,
      upcomingPackage: renewalPlan.upcomingPackage,
      isFreeTrial: isFreeTrial(renewalPlan),
      isFreeTrialWithoutPurchasedPlan: isFreeTrialWithoutPurchasedPlan(renewalPlan),
      renewalPlanLoaded: renewalPlan.loaded,
    };
  }
  return {
    ...result,
    isFreeTrial: false,
    isFreeTrialWithoutPurchasedPlan: false,
    renewalPlanLoaded: false,
  };
};

export default useMonetizationStatus;
